import Hero from "../components/hero";
import Help from "../components/help";
import heroImage from "@assets/images/investor-relations-hero.svg";
import documentIcon from "@assets/icons/document.svg";
import downloadIcon from "@assets/icons/download-arrow.svg";
import regulatoryIcon from "@assets/icons/regulatory-compliance.svg";
import financialIcon from "@assets/icons/financial-integrity.svg";
import riskIcon from "@assets/icons/risk-management.svg";
import procurementIcon from "@assets/icons/procurement-governance.svg";
import dataIcon from "@assets/icons/data-protection.svg";
import environmentalIcon from "@assets/icons/environmental.svg";
import socialIcon from "@assets/icons/social.svg";
import governanceIcon from "@assets/icons/financial-integrity.svg";

// ── DATA ──────────────────────────────────────────────────────────────────────

const heroData = {
  title: "Reliable Utility Operations. Strong Governance. Sustainable Growth.",
  description:
    "GAZ is a licensed LPG/SNG gas services provider operating under the Contra Group of Companies. We deliver regulated utility operations, transparent billing, and long-term infrastructure management across residential, commercial, and industrial sectors in the UAE.",
  image: heroImage,
  buttonText: "Download Corporate Profile",
  buttonOnclick: () => {},
  // openMailto({ subject: "Corporate Profile Download Request" }),
};

const investmentCards = [
  {
    title: "Essential Utility Services",
    desc: "Core infrastructure delivering critical energy services to communities",
  },
  {
    title: "Recurring Revenue",
    desc: "Stable income from metering, billing & maintenance contracts",
  },
  {
    title: "Long-term Contracts",
    desc: "Partnerships with developers, property managers & commercial clients",
  },
  {
    title: "Regulatory Alignment",
    desc: "Full compliance with DCD, ESMA, FTA & UAE Fire & Life Safety Code",
  },
  {
    title: "Predictable Cash Flows",
    desc: "Low operational volatility with stable utility demand",
  },
];

const governanceCompliance = [
  {
    icon: regulatoryIcon,
    title: "Regulatory Compliance",
    desc: "Aligned with DCD, ESMA, IFTs, our UAE Fire & Life Safety Code standards.",
  },
  {
    icon: financialIcon,
    title: "Financial Integrity",
    desc: "Transparent invoicing, audit-ready statements, VAT compliance.",
  },
  {
    icon: riskIcon,
    title: "Risk Management",
    desc: "Safety audits, operational controls, HSE monitoring, insurance coverage.",
  },
  {
    icon: procurementIcon,
    title: "Procurement Governance",
    desc: "Fair vendor selection, technical evaluation, ethical sourcing.",
  },
  {
    icon: dataIcon,
    title: "Data Protection",
    desc: "PDPL (Federal Decree-Law No. 45/2021) compliance & secure digital platforms.",
  },
];

const groupOperatesAcross = [
  "Real Estate",
  "Energy Management",
  "Education",
  "Facility Services",
  "Technology",
  "F&B Operations",
  "Security",
];

const whereWeOperate = [
  "Residential apartment blocks",
  "Commercial Buildings",
  "Industrial facilities",
  "Hotels & Hospitality",
  "Retail & F&B outlets",
  "Large-scale developments",
];

const coreCapabilities = [
  "LPG/SNG Network Design & Engineering",
  "Metering & Smart Metering Solutions",
  "Utility Billing & Collections",
  "Preventive Maintenance & Emergency Services",
  "Safety Inspections & Certification",
  "Developer & Property Management",
];

const esgCommitments = [
  {
    icon: environmentalIcon,
    title: "Environmental",
    desc: "Leak reduction, energy-efficient systems, smart metering rollout.",
  },
  {
    icon: socialIcon,
    title: "Social",
    desc: "Community safety campaigns, customer education, staff development.",
  },
  {
    icon: governanceIcon,
    title: "Governance",
    desc: "Transparent billing, fair environmental controls, ethics-first operations.",
  },
];

const esgAlignmentPoints = [
  "UAE Net Zero 2050 Initiative",
  "UN Sustainable Development Goals (SDGs)",
  "ISO 14001 & 45001 safety and environmental frameworks",
];

const financialMetrics = [
  { label: "Placeholder (Figures)", sublabel: "Connected Units" },
  { label: "Placeholder (Figures)", sublabel: "Annual Utility Throughput" },
  { label: "Placeholder (Figures)", sublabel: "Service Centre Growth" },
  { label: "Placeholder (Figures)", sublabel: "Customer Satisfaction" },
  { label: "Placeholder (Figures)", sublabel: "Operational Uptime" },
  { label: "Placeholder (Figures)", sublabel: "Emergency Response" },
];

const downloads = [
  { title: "Corporate Profile" },
  { title: "ESG & Sustainability Snapshot" },
  { title: "Trade License" },
  { title: "Certifications (DCD, ESMA, TTA)" },
  { title: "Organisational Structure" },
  { title: "Governance Compliance Statement" },
];

// ── COMPONENT ─────────────────────────────────────────────────────────────────

function InvestorRelations() {
  return (
    <div className="bg-white">
      <Hero data={heroData} />

      {/* ── ABOUT GAZ AS AN INVESTMENT ──────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            About GAZ as an Investment
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            GAZ offers a stable, compliance-driven business model built on
            essential infrastructure services.
          </p>

          <div className="grid gap-3 mt-8 md:gap-6 lg:mt-16 md:grid-cols-2 lg:grid-cols-3">
            {investmentCards.map((card, index) => (
              <article
                key={index}
                className="p-4 md:p-6 border border-[#62636C33] rounded-[8px] md:rounded-[16px] text-center"
              >
                <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl mb-3 md:mb-6">
                  {card.title}
                </h3>
                <p className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                  {card.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── CORPORATE GOVERNANCE & COMPLIANCE ────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Corporate Governance &amp; Compliance
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            Our Commitment to Ethical and Transparent Operations
          </p>

          <div className="grid gap-4 mt-8 lg:mt-16 md:grid-cols-2 lg:grid-cols-3">
            {governanceCompliance.map((item, index) => (
              <article
                key={index}
                className="p-4 md:p-6 bg-white border border-[#62636C33] rounded-[12px] md:rounded-[24px] flex flex-col items-center text-center"
              >
                <img
                  src={item.icon}
                  alt={item.title}
                  className="mb-3 md:mb-6 size-8 md:size-[70px]"
                />
                <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl mb-3">
                  {item.title}
                </h3>
                <p className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                  {item.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── OWNERSHIP & GROUP AFFILIATION ─────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto flex flex-col xl:flex-row gap-10 justify-between items-start xl:items-center">
          {/* Left */}
          <div className="xl:max-w-[560px]">
            <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[48px] font-semibold">
              Ownership &amp; Group Affiliation
            </h2>
            <p className="mt-4 text-[14px] md:text-base text-[#62636C] leading-relaxed">
              GAZ is a utility and energy services subsidiary of the Contra
              Group of Companies — a multi-sector conglomerate established in
              1960 in Abu Dhabi and re-founded in 2006 by Yazan Bin Mohammad.
            </p>
            <p className="mt-4 text-[14px] md:text-base text-[#62636C] leading-relaxed">
              GAZ serves as the group's specialized gas distribution,
              metering, and billing entity, supporting the group's
              infrastructure strategy across the UAE.
            </p>
          </div>

          {/* Right — group sectors */}
          <div className="xl:max-w-[640px] w-full p-4 md:p-8 border border-[#62636C33] rounded-[12px] md:rounded-[24px]">
            <p className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl mb-5">
              The group operates across:
            </p>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              {groupOperatesAcross.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="flex-shrink-0 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                  <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── OPERATIONAL FOOTPRINT & CAPABILITIES ─────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Operational Footprint &amp; Capabilities
          </h2>

          <div className="grid gap-4 mt-8 lg:mt-16 md:grid-cols-2">
            {/* Where We Operate */}
            <article className="p-4 md:p-8 bg-white border border-[#62636C33] rounded-[12px] md:rounded-[24px]">
              <h3 className="text-[#269D0E] font-semibold text-sm md:text-xl lg:text-[40px] mb-4">
                Where We Operate
              </h3>
              <ul className="space-y-2">
                {whereWeOperate.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#269D0E]" />
                    <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </article>

            {/* Core Capabilities */}
            <article className="p-4 md:p-8 bg-white border border-[#62636C33] rounded-[12px] md:rounded-[24px]">
              <h3 className="text-gis-red font-semibold text-sm md:text-xl lg:text-[40px] mb-4">
                Core Capabilities
              </h3>
              <ul className="space-y-2">
                {coreCapabilities.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-gis-red" />
                    <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* ── ESG COMMITMENT ────────────────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            ESG Commitment
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            We align our operations with sustainability principles for a better
            tomorrow.
          </p>

          <div className="grid gap-4 mt-8 lg:mt-16 md:grid-cols-3">
            {esgCommitments.map((item, index) => (
              <article
                key={index}
                className="p-4 md:p-6 border border-[#62636C33] rounded-[12px] md:rounded-[24px] text-center"
              >
                <img
                  src={item.icon}
                  alt={item.title}
                  className="mx-auto mb-3 md:mb-6 size-10 md:size-14"
                />
                <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl mb-2">
                  {item.title}
                </h3>
                <p className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                  {item.desc}
                </p>
              </article>
            ))}
          </div>

          {/* ESG Alignment */}
          <div className="text-[14px] md:text-xs lg:text-base leading-tight text-[#62636C] mt-8 lg:mt-12 px-3 md:px-5 py-3 bg-[#FEF6F4] border border-[#F03813] rounded-lg">
            We align our operations with sustainability principles under:
            <ul className="mt-3 space-y-2">
              {esgAlignmentPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                  <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── FINANCIAL OVERVIEW ────────────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Financial Overview
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            Key performance metrics and operational indicators.
          </p>

          <div className="grid gap-3 mt-8 md:gap-6 lg:mt-16 md:grid-cols-3">
            {financialMetrics.map((metric, index) => (
              <article
                key={index}
                className="p-4 md:p-6 bg-white border border-[#62636C33] rounded-[8px] md:rounded-[16px] text-center"
              >
                <p className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl">
                  {metric.label}
                </p>
                <p className="mt-1 text-[13px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                  {metric.sublabel}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── INVESTOR DOCUMENTS & DOWNLOADS ───────────────────────────── */}
      <section className="px-5 py-10 md:py-16 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Investor Documents &amp; Downloads
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            Access key corporate documents and compliance materials.
          </p>

          <div className="grid gap-4 mt-8 lg:grid-cols-2 md:mt-16">
            {downloads.map((resource, index) => (
              <article
                key={index}
                className="bg-white p-4 md:p-6 border border-[#62636C33] rounded-[8px] md:rounded-[16px] flex items-center justify-between gap-4"
              >
                <div className="flex flex-row items-center gap-4 xl:gap-8">
                  <div className="bg-[#F1F1F1] p-2 rounded-[12px] flex-shrink-0">
                    <img
                      src={documentIcon}
                      alt="document icon"
                      className="size-5 md:size-12"
                    />
                  </div>
                  <h4 className="text-[#1E1F24] font-normal text-xs md:text-base lg:text-xl">
                    {resource.title}
                  </h4>
                </div>
                <button className="flex-shrink-0 size-8 md:size-10 hover:bg-[#F1F1F1] border border-[#F1F1F1] rounded-full grid place-content-center">
                  <img
                    src={downloadIcon}
                    alt="download icon"
                    className="size-5 md:size-7 invert"
                  />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── HELP CTA ──────────────────────────────────────────────────── */}
      <Help title="For investor inquiries or corporate information:" />
    </div>
  );
}

export default InvestorRelations;
